import Typography from '@components/common/typography';
import { Icon, type TIconType } from '@components/icons';

import styles from './FilterDrawer.module.scss';

export type SortDir = 'newest' | 'oldest';
export type MoneyMovementValue = 'all' | 'in' | 'out';

export interface FilterStatusChip<TStatus extends string | number> {
    label: string;
    value: TStatus;
}

export interface FilterMoneyMovement {
    value: MoneyMovementValue;
    onChange: (v: MoneyMovementValue) => void;
}

export interface FilterDrawerProps<TStatus extends string | number> {
    dateFrom: string;
    dateTo: string;
    onDateFrom: (v: string) => void;
    onDateTo: (v: string) => void;
    /** Sales-only "Money movement" radio section; omit to hide it (orders has none). */
    movement?: FilterMoneyMovement;
    statusChips: FilterStatusChip<TStatus>[];
    selectedStatuses: TStatus[];
    onToggleStatus: (v: TStatus) => void;
    sortDir: SortDir;
    onSortDir: (v: SortDir) => void;
    onReset: () => void;
    onClose: () => void;
}

const MOVEMENT_OPTIONS: { value: MoneyMovementValue; label: string; icon: TIconType }[] = [
    { value: 'all', label: 'All', icon: 'money_in' },
    { value: 'in', label: 'Money in', icon: 'money_in' },
    { value: 'out', label: 'Money out', icon: 'money_out' }
];

// Shared 712px right-side filter drawer — date range + optional money-movement
// radio group + status chips + sort radio. Extracted from the near-identical
// OrderFilterPanel/SaleFilterPanel copies in orders/index.tsx and
// sales/index.tsx; see dev-portal enterprise redesign spec §4.9/§6.
const FilterDrawer = <TStatus extends string | number>({
    dateFrom,
    dateTo,
    onDateFrom,
    onDateTo,
    movement,
    statusChips,
    selectedStatuses,
    onToggleStatus,
    sortDir,
    onSortDir,
    onReset,
    onClose
}: FilterDrawerProps<TStatus>) => (
    <div className={styles.overlay} onClick={onClose}>
        <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.header}>
                <p className={styles.title}>Filters</p>
                <div className={styles.header_actions}>
                    <button type="button" className={styles.reset_link} onClick={onReset}>
                        Clear all
                    </button>
                    <button
                        type="button"
                        className={styles.close_btn}
                        onClick={onClose}
                        aria-label="Close filters">
                        <Icon name="close" width={18} height={18} />
                    </button>
                </div>
            </div>
            <div className={styles.body}>
                <div className={styles.section}>
                    <Typography variant="body3" weight="semibold" className={styles.section_title}>
                        Date
                    </Typography>
                    <p className={styles.section_hint}>Select a date range</p>
                    <div className={styles.date_box}>
                        <input
                            type="date"
                            value={dateFrom}
                            className={styles.date_input}
                            aria-label="Date from"
                            onChange={(e) => onDateFrom(e.target.value)}
                        />
                        <span className={styles.date_sep}>–</span>
                        <input
                            type="date"
                            value={dateTo}
                            className={styles.date_input}
                            aria-label="Date to"
                            onChange={(e) => onDateTo(e.target.value)}
                        />
                        <span className={styles.date_icon}>
                            <Icon name="calendar" width={18} height={18} />
                        </span>
                    </div>
                </div>

                {movement && (
                    <div className={styles.section}>
                        <Typography
                            variant="body3"
                            weight="semibold"
                            className={styles.section_title}>
                            Money movement
                        </Typography>
                        {MOVEMENT_OPTIONS.map((opt) => (
                            <label key={opt.value} className={styles.radio_row}>
                                <input
                                    type="radio"
                                    name="filterDrawerMovement"
                                    checked={movement.value === opt.value}
                                    onChange={() => movement.onChange(opt.value)}
                                    className={styles.radio_input}
                                />
                                <Icon
                                    name={opt.icon}
                                    width={18}
                                    height={18}
                                    className={styles.radio_icon}
                                />
                                <span className={styles.radio_label}>{opt.label}</span>
                            </label>
                        ))}
                    </div>
                )}

                <div className={styles.section}>
                    <Typography variant="body3" weight="semibold" className={styles.section_title}>
                        Status
                    </Typography>
                    <div className={styles.chips_row}>
                        {statusChips.map((chip) => (
                            <button
                                key={chip.value}
                                type="button"
                                className={[
                                    styles.chip,
                                    selectedStatuses.includes(chip.value) ? styles.chip_active : ''
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                onClick={() => onToggleStatus(chip.value)}>
                                {chip.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className={styles.section}>
                    <Typography variant="body3" weight="semibold" className={styles.section_title}>
                        Sort by
                    </Typography>
                    <label className={styles.radio_row}>
                        <input
                            type="radio"
                            name="filterDrawerSort"
                            checked={sortDir === 'newest'}
                            onChange={() => onSortDir('newest')}
                            className={styles.radio_input}
                        />
                        <span className={styles.radio_label}>Date: Newest first</span>
                    </label>
                    <label className={styles.radio_row}>
                        <input
                            type="radio"
                            name="filterDrawerSort"
                            checked={sortDir === 'oldest'}
                            onChange={() => onSortDir('oldest')}
                            className={styles.radio_input}
                        />
                        <span className={styles.radio_label}>Date: Oldest first</span>
                    </label>
                </div>
            </div>
        </div>
    </div>
);

export default FilterDrawer;
